import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'

interface EmailPreview {
  to: string
  subject: string
  body: string
}

interface EmailSendModalProps {
  /** Endpoint relativ la /api, fara metoda. Ex: '/marketing/events/12' */
  baseEndpoint: string
  previewSubpath: string // ex: '/email-preview'
  sendSubpath: string // ex: '/send-confirmation'
  title: string
  b4lPlaceholder: string // ex: '[B4L-EVENT-XXXXXX]'
  onClose: () => void
  onSent: () => void
}

export function EmailSendModal({
  baseEndpoint,
  previewSubpath,
  sendSubpath,
  title,
  b4lPlaceholder,
  onClose,
  onSent,
}: EmailSendModalProps) {
  const previewQuery = useQuery<EmailPreview>({
    queryKey: ['email-preview', baseEndpoint, previewSubpath],
    queryFn: async () => (await api.get<EmailPreview>(`${baseEndpoint}${previewSubpath}`)).data,
  })

  const sendMutation = useMutation({
    mutationFn: async () => (await api.post(`${baseEndpoint}${sendSubpath}`)).data,
    onSuccess: (data: { b4lId: string }) => {
      toast.success(`Email pus în coadă (${data.b4lId})`)
      onSent()
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la trimitere'
      toast.error(message)
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() => sendMutation.mutate()}
            loading={sendMutation.isPending}
            disabled={!previewQuery.data}
          >
            Confirmă trimiterea
          </Button>
        </>
      }
    >
      {previewQuery.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se generează previzualizarea…</p>
      ) : previewQuery.isError ? (
        <p className="text-sm text-b4l-red">
          Previzualizarea nu a putut fi generată. Închide fereastra și încearcă din nou.{' '}
          {(previewQuery.error as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message ?? ''}
        </p>
      ) : previewQuery.data ? (
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-xs text-b4l-text-muted">Către:</span>{' '}
            <span className="font-mono">{previewQuery.data.to}</span>
          </div>
          <div>
            <span className="text-xs text-b4l-text-muted">Subiect:</span>{' '}
            <span>
              <span className="font-mono text-b4l-purple-dark">{b4lPlaceholder} </span>
              {previewQuery.data.subject}
            </span>
          </div>
          <div>
            <div className="mb-1 text-xs text-b4l-text-muted">Conținut:</div>
            <pre className="whitespace-pre-wrap rounded border border-b4l-border bg-b4l-bg-soft p-3 font-sans text-xs">
              {previewQuery.data.body}
            </pre>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
